import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useFormProgress } from '../../../hooks/useFormProgress';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { fetchApplicationDetails, uploadFormFile, saveFormData } from '../../../utils/apiUtils';
import styled, { createGlobalStyle } from 'styled-components';
import FilePreviewModal from '../../common/FilePreviewModal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FormPageNavigator from '../../common/FormPageNavigator';
import SessionTimer from '../../common/SessionTimer';
import ApplicationIdDisplay from '../../common/ApplicationIdDisplay';

// API URL configuration
const API_URL_CONFIG = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL_CONFIG.endsWith('/api') ? API_URL_CONFIG.slice(0, -4) : API_URL_CONFIG;

// Helper function to generate a unique application ID
const generateApplicationId = (): string => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
  const year = String(now.getFullYear());
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
  const randomChar = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)];
  return `CNG-${day}${month}${year}${hours}${minutes}${seconds}-${milliseconds}${randomChar}`;
};

// A4 Dimensions (approximate for screen, maintaining aspect ratio)
// 210mm / 297mm = 0.707
const A4_WIDTH_PX = 794; // Approx 210mm at 96 DPI
const A4_HEIGHT_PX = 1123; // Approx 297mm at 96 DPI

// Progress bar at top of page
const ProgressBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #6cb288;
  transition: width 0.5s ease-in-out;
`;

const ProgressLabel = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: #f8fcfa;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 1000;
`;

// Zoom controls
const ZoomControlsContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
`;

const ZoomButton = styled.button`
  width: 30px;
  height: 30px;
  background-color: white;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  padding: 0;
  
  &:hover {
    background-color: #f8fcfa;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

// Global style to inject shake keyframes into global CSS for inline style usage
const GlobalShakeStyle = createGlobalStyle`
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
    20%, 40%, 60%, 80% { transform: translateX(8px); }
  }
`;

const OfflineIndicator = styled.div`
  background-color: #dc3545; /* Red for offline */
  color: white;
  text-align: center;
  padding: 10px;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2000; /* Ensure it's above other elements */
  font-size: 0.9em;
`;

const PageWrapper = styled.div<{ scale: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 100vh;
  background-color: #f5f5f5;
  transform: scale(${props => props.scale});
  transform-origin: center top;
  transition: transform 0.3s ease;
`;

const A4PageContainer = styled.div`
  width: ${A4_WIDTH_PX}px;
  min-height: ${A4_HEIGHT_PX}px; /* Use min-height to allow content to expand if needed */
  background-color: white;
  padding: 50px; /* Increased padding for more breathing room */
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07); /* Softer, more modern shadow */
  box-sizing: border-box;
  position: relative; /* For page number positioning */
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  color: #212529; /* Darker, more standard text color */
  font-size: 13px; /* Base font size for content */
  line-height: 1.6;
`;

const FloatingBackButton = styled.button`
  position: fixed;
  bottom: 30px;
  left: 30px;
  background: linear-gradient(145deg, #4a6cf7, #5e54e2); /* Modern gradient */
  color: white;
  border: none;
  border-radius: 50%; /* Circular button */
  width: 60px; /* Size of the button */
  height: 60px;
  font-size: 22px; /* Slightly smaller icon size for a cleaner look */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(74, 108, 247, 0.3), 0 3px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000; /* Ensure it's on top */
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 10px 20px rgba(74, 108, 247, 0.4), 0 6px 6px rgba(0, 0, 0, 0.1);
    
    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow: 0 3px 8px rgba(74, 108, 247, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &::after {
    content: 'Back';
    position: absolute;
    font-size: 12px;
    bottom: 7px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const FormContentContainer = styled.div`
  /* Form content container */
`;

const FormFieldFullWidth = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;

const Label = styled.label`
  margin-right: 8px;
  white-space: nowrap;
  font-weight: 500;
  color: #495057;
  font-size: 0.9em;
  margin-bottom: 4px;
`;

const StyledInput = styled.input`
  flex-grow: 1;
  border: 1px solid #a8d0b9;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 1em;
  font-family: inherit;
  background-color: #f8fcfa;
  color: #495057;
  line-height: 1.5;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #6cb288;
    box-shadow: 0 0 0 0.2rem rgba(108, 178, 136, 0.25);
  }

  &:hover {
    border-color: #8ac0a0;
  }

  &::placeholder {
    color: #7ea38c;
    opacity: 1; 
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  border: 1px solid #a8d0b9;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 1em;
  font-family: inherit;
  background-color: #f8fcfa;
  color: #495057;
  line-height: 1.5;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #6cb288;
    box-shadow: 0 0 0 0.2rem rgba(108, 178, 136, 0.25);
  }

  &:hover {
    border-color: #8ac0a0;
  }

  &::placeholder {
    color: #7ea38c;
    opacity: 1; 
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 5px;
  margin-bottom: 10px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: 6px;
  
  input[type="radio"] {
    appearance: none;
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border: 2px solid #6cb288;
    border-radius: 50%;
    outline: none;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;
    
    &:checked {
      border-color: #6cb288;
      background-color: #f0f9f4;
      
      &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: 3px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #6cb288;
      }
    }
  }
  
  &:hover input[type="radio"] {
    border-color: #4d9e70;
    box-shadow: 0 0 0 1px rgba(108, 178, 136, 0.2);
  }
`;

const InlineGroup = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  
  > div {
    flex: 1;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1em;
  font-weight: 600;
  color: #495057;
  margin-top: 20px;
  margin-bottom: 10px;
`;

const PageNumber = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  font-size: 0.8em;
  color: #777;
`;

const NextPageButton = styled.button`
  position: absolute;
  bottom: 15px;
  right: 60px;
  background-color: #6cb288;
  color: white;
  padding: 8px 25px;
  border: none;
  border-radius: 4px;
  font-size: 1em;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #4d9e70;
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const SaveIndicator = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 50%;
  left: 20px;
  transform: translateY(-50%);
  background-color: #f8fcfa;
  color: #6cb288;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #a8d0b9;
  font-size: 0.8em;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 10000;
`;

const SaveDraftButton = styled.button`
  background-color: #ffc107;
  color: #212529;
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 15px;
  margin-right: 10px;
  display: inline-block;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #e0a800;
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #c69500;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }
`;

const FileUploadButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 0.9em;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const PayNowButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 30px;
  display: block; 
  margin-left: auto;
  margin-right: auto;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #0069d9;
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #0056b3;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }
`;

const ActionButtonsContainer = styled.div`
  margin-top: 30px;
  width: 100%;
`;

interface FormData {
  applicationNoOfficeUse: string;
  // From Page1 - for display
  nameInBlocks?: string;
  partnerDirectorDetails?: Array<{
    id: string;
    nameInBlocks: string;
    [key: string]: any;
  }>;
  dealershipType?: string;
  // DECLARATION & SIGNATURE
  declarantName: string;
  declarantParentName: string;
  signatureFullName: string;
  declarationPlace: string;
  declarationDate: string;
  signatureFile?: File | string | null;
  errors?: {
    // Add error fields as needed
  };
}

interface ApplicationFormPage3Props {
  formData?: any;
  isViewMode?: boolean;
  editingUser?: 'main' | number | null;
}

const ApplicationFormPage3: React.FC<ApplicationFormPage3Props> = ({ isViewMode = false, formData: propFormData }) => {
  const formRef = useRef<HTMLDivElement>(null);
  
  // Application lock status (locked after payment or manually locked by admin) - declare early
  const [isApplicationLocked, setIsApplicationLocked] = useState<boolean>(false);
  
  // Read editingUser from sessionStorage (set by Page1's partner selection modal)
  const [editingUser, setEditingUser] = useState<'main' | number | null>(() => {
    const stored = sessionStorage.getItem('editingUser');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  });
  
  // Check if main applicant section should be locked (when a partner is editing)
  const isMainApplicantSectionLocked = editingUser !== null && editingUser !== 'main';
  // Helper: Combines conditions that should disable main applicant fields
  // Now also includes isApplicationLocked for paid/manually locked applications
  const isMainFieldDisabled = isMainApplicantSectionLocked || isApplicationLocked;
  
  // Check if a specific partner's section is locked
  // Partners can only edit their own section, not main applicant or other partners
  const isPartnerSectionLocked = (partnerIndex: number) => {
    if (editingUser === null) return false; // No selection yet
    if (editingUser === 'main') return false; // Main applicant can edit all
    return editingUser !== partnerIndex; // Only the selected partner can edit their section
  };
  
  const navigate = useNavigate();
  const location = useLocation();

  const [applicationId, setApplicationId] = useState<string>('');
  const isNewlyGeneratedIdRef = useRef<boolean>(false);
  
  // Zoom state
  const [zoomScale, setZoomScale] = useState<number>(1.0);
  
  // Check if Fill Dummy Data button should be shown (controlled from Admin Controls, default OFF)
  const [showDummyDataButton, setShowDummyDataButton] = useState<boolean>(false);
  
  // Fetch showDummyDataButton setting from backend
  useEffect(() => {
    const fetchDummyDataSetting = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const API_BASE = API_URL === '/api' ? '' : API_URL;
        const response = await fetch(`${API_BASE}/api/public/settings`);
        if (response.ok) {
          const data = await response.json();
          const settings = data.settings || data;
          if (settings && typeof settings.showDummyDataButton === 'boolean') {
            setShowDummyDataButton(settings.showDummyDataButton);
          }
        }
      } catch (error) {
        console.error('Error fetching dummy data button setting:', error);
      }
    };
    fetchDummyDataSetting();
  }, []);
  
  // Form data state
  const [formData, setFormData] = useState<FormData>(propFormData || {
    applicationNoOfficeUse: '',
    // From Page1 - for display
    nameInBlocks: '',
    partnerDirectorDetails: [],
    dealershipType: '',
    // DECLARATION & SIGNATURE
    declarantName: '',
    declarantParentName: '',
    signatureFullName: '',
    declarationPlace: '',
    declarationDate: '',
    signatureFile: null,
    errors: {}
  });

  // State for save indicator
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string>('Saved');
  const [showSaveIndicator, setShowSaveIndicator] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [showBrochureModal, setShowBrochureModal] = useState<boolean>(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalFileUrl, setModalFileUrl] = useState<string | null>(null);
  const [modalFileName, setModalFileName] = useState<string | null>(null);
  const [modalFileType, setModalFileType] = useState<string | null>(null);
  
  // Signature preview state
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string | null>(null);
  const [signatureValidationError, setSignatureValidationError] = useState<string | null>(null);
  const [isValidatingSignature, setIsValidatingSignature] = useState<boolean>(false);
  const [partnerSignaturePreviews, setPartnerSignaturePreviews] = useState<{ [key: number]: string }>({});
  const [partnerSignatureErrors, setPartnerSignatureErrors] = useState<{ [key: number]: string }>({});
  const [partnerSignatureUploading, setPartnerSignatureUploading] = useState<{ [key: number]: boolean }>({});
  
  // Save timeout reference for debouncing
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Signature mandatory field validation state and ref
  const signatureRowRef = useRef<HTMLTableRowElement>(null);
  const [isSignatureShaking, setIsSignatureShaking] = useState<boolean>(false);

  // Validate signature image using refined AI-like heuristics
  // Allows: handwritten signatures, stamps, signatures on ruled paper
  const validateSignatureImage = async (file: File): Promise<{ isValid: boolean; error?: string; canRetryWithBgRemoval?: boolean }> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Basic dimension checks
          if (img.width < 30 || img.height < 15) {
            resolve({ isValid: false, error: 'Image is too small. Please upload a clear signature image (min 30x15 pixels).' });
            return;
          }
          
          if (img.width > 4000 || img.height > 4000) {
            resolve({ isValid: false, error: 'Image is too large. Please upload a smaller signature image (max 4000x4000 pixels).' });
            return;
          }

          // Check file size
          if (file.size > 5 * 1024 * 1024) {
            resolve({ isValid: false, error: 'File size too large. Please upload a signature image under 5MB.' });
            return;
          }

          // Create canvas to analyze image content
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve({ isValid: true }); // Can't analyze, assume valid
            return;
          }

          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          try {
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            
            // Analyze image for signature-like content
            let inkPixels = 0;
            let blueLinePixels = 0; // For ruled paper detection
            let totalPixels = data.length / 4;
            
            for (let i = 0; i < data.length; i += 4) {
              const r = data[i];
              const g = data[i + 1];
              const b = data[i + 2];
              const a = data[i + 3];
              
              // Check if pixel is ink (dark enough)
              const brightness = (r + g + b) / 3;
              if (a > 128 && brightness < 200) {
                inkPixels++;
              }
              
              // Detect blue ruled lines (common in notebooks)
              if (b > r + 30 && b > g + 30 && b > 100) {
                blueLinePixels++;
              }
            }
            
            const inkRatio = inkPixels / totalPixels;
            const blueRatio = blueLinePixels / totalPixels;
            
            // If has blue lines (ruled paper), it's likely valid
            const hasRuledLines = blueRatio > 0.01 && blueRatio < 0.15;
            
            // Very lenient validation - signatures, stamps, ruled paper all allowed
            if (inkRatio < 0.002 && !hasRuledLines) {
              resolve({ 
                isValid: false, 
                error: 'Image appears to be blank or too faint. Please upload a clear signature with visible ink.',
                canRetryWithBgRemoval: false
              });
              return;
            }
            
            // If too much ink (possible photo), suggest background removal
            if (inkRatio > 0.8) {
              resolve({ 
                isValid: false, 
                error: 'Image appears to be a photo. Try removing the background or use a clearer signature image.',
                canRetryWithBgRemoval: true
              });
              return;
            }
            
            // Valid signature (including stamps and ruled paper signatures)
            resolve({ isValid: true });
          } catch (err) {
            // If analysis fails, assume valid
            resolve({ isValid: true });
          }
        };
        
        img.onerror = () => {
          resolve({ isValid: false, error: 'Could not load image. Please ensure you upload a valid image file (JPEG, PNG).' });
        };
        
        img.src = e.target?.result as string;
      };
      
      reader.onerror = () => {
        resolve({ isValid: false, error: 'Could not read file. Please try again.' });
      };
      
      reader.readAsDataURL(file);
    });
  };

  // State for background removal option
  const [showBgRemovalOption, setShowBgRemovalOption] = useState<boolean>(false);
  const [pendingSignatureFile, setPendingSignatureFile] = useState<File | null>(null);

  // Handle signature file upload with validation (Images only, no PDF)
  const handleSignatureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reject PDF uploads - only accept images for signature
    if (file.type === 'application/pdf') {
      setSignatureValidationError('PDF files are not accepted for signature. Please upload an image file (JPEG, PNG) of your signature.');
      e.target.value = '';
      return;
    }

    // Check if it's an image file
    if (!file.type.startsWith('image/')) {
      setSignatureValidationError('Please upload an image file (JPEG, PNG) for your signature.');
      e.target.value = '';
      return;
    }

    setIsValidatingSignature(true);
    setSignatureValidationError(null);
    setShowBgRemovalOption(false);

    const validation = await validateSignatureImage(file);

    if (!validation.isValid) {
      setSignatureValidationError(validation.error || 'Invalid signature image.');
      // If background removal might help, show the option
      if (validation.canRetryWithBgRemoval) {
        setShowBgRemovalOption(true);
        setPendingSignatureFile(file);
      }
      setIsValidatingSignature(false);
      e.target.value = ''; // Reset file input
      return;
    }

    // Validation passed - upload file and generate preview
    await uploadSignatureFile(file);
  };

  // Upload signature file to backend
  const uploadSignatureFile = async (file: File) => {
    if (applicationId) {
      try {
        const response = await uploadFormFile(applicationId, file, 'signatureFile', () => {});
        if (response && response.success && response.filePath) {
          // Generate preview URL
          const previewUrl = await generateSignaturePreview(response.filePath);
          setSignaturePreviewUrl(previewUrl);
          setFormData(prev => ({ ...prev, signatureFile: response.filePath }));
          setShowSaveIndicator(true);
          setSaveMessage('Saved');
          debouncedSave({ ...formData, signatureFile: response.filePath });
          setShowBgRemovalOption(false);
          setPendingSignatureFile(null);
        }
      } catch (error) {
        console.error('Error uploading signature file:', error);
        setSignatureValidationError('Failed to upload signature. Please try again.');
      } finally {
        setIsValidatingSignature(false);
      }
    } else {
      // No applicationId yet, just store locally
      const previewUrl = URL.createObjectURL(file);
      setSignaturePreviewUrl(previewUrl);
      setFormData(prev => ({ ...prev, signatureFile: file }));
      setIsValidatingSignature(false);
    }
  };

  // Generate signature preview URL
  const generateSignaturePreview = async (filePath: string): Promise<string> => {
    const rawApiBaseUrl = process.env.REACT_APP_API_URL || '';
    let serverBase = window.location.origin;
    if (rawApiBaseUrl.startsWith('http')) {
      try {
        const parsed = new URL(rawApiBaseUrl);
        serverBase = `${parsed.protocol}//${parsed.host}`;
      } catch (e) {
        serverBase = window.location.origin;
      }
    }
    if (serverBase.includes('localhost:3000')) {
      serverBase = serverBase.replace(':3000', ':5000');
    }
    const imgPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
    return `${serverBase}${imgPath}`;
  };

  // Handle retry with background removal (simple client-side approach)
  const handleRemoveBackground = async () => {
    if (!pendingSignatureFile) return;
    
    setIsValidatingSignature(true);
    setSignatureValidationError(null);
    
    try {
      // Create canvas for background removal
      const reader = new FileReader();
      reader.onload = async (e) => {
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            setSignatureValidationError('Could not process image. Please try a different image.');
            setIsValidatingSignature(false);
            return;
          }
          
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          
          // Get image data and remove near-white background
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const brightness = (r + g + b) / 3;
            
            // Make near-white pixels transparent
            if (brightness > 220) {
              data[i + 3] = 0; // Set alpha to 0
            }
          }
          
          ctx.putImageData(imageData, 0, 0);
          
          // Convert canvas to blob
          canvas.toBlob(async (blob) => {
            if (blob) {
              const processedFile = new File([blob], pendingSignatureFile.name.replace(/\.[^.]+$/, '.png'), { type: 'image/png' });
              await uploadSignatureFile(processedFile);
              setShowBgRemovalOption(false);
              setPendingSignatureFile(null);
            } else {
              setSignatureValidationError('Could not process image. Please try a different image.');
              setIsValidatingSignature(false);
            }
          }, 'image/png');
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(pendingSignatureFile);
    } catch (error) {
      console.error('Error removing background:', error);
      setSignatureValidationError('Failed to remove background. Please try a different image.');
      setIsValidatingSignature(false);
    }
  };

  // Handle partner signature upload (Images only - no PDF)
  const handlePartnerSignatureUpload = async (e: React.ChangeEvent<HTMLInputElement>, partnerIndex: number) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reject PDF uploads - only accept images for signature
    if (file.type === 'application/pdf') {
      setPartnerSignatureErrors(prev => ({ ...prev, [partnerIndex]: 'PDF files are not accepted for signature. Please upload an image file (JPEG, PNG).' }));
      e.target.value = '';
      return;
    }

    // Check if it's an image file
    if (!file.type.startsWith('image/')) {
      setPartnerSignatureErrors(prev => ({ ...prev, [partnerIndex]: 'Please upload an image file (JPEG, PNG) for signature.' }));
      e.target.value = '';
      return;
    }

    // Only accept jpg, jpeg, png
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      setPartnerSignatureErrors(prev => ({ ...prev, [partnerIndex]: 'Only JPG, JPEG, and PNG files are accepted for signature.' }));
      e.target.value = '';
      return;
    }

    const fieldName = `partner_${partnerIndex}_signatureFile`;
    
    // Set uploading state
    setPartnerSignatureUploading(prev => ({ ...prev, [partnerIndex]: true }));
    setPartnerSignatureErrors(prev => ({ ...prev, [partnerIndex]: '' }));

    const validation = await validateSignatureImage(file);

    if (!validation.isValid) {
      setPartnerSignatureErrors(prev => ({ ...prev, [partnerIndex]: validation.error || 'Invalid signature image.' }));
      setPartnerSignatureUploading(prev => ({ ...prev, [partnerIndex]: false }));
      e.target.value = '';
      return;
    }

    // Upload to backend
    if (applicationId) {
      try {
        const response = await uploadFormFile(applicationId, file, fieldName, () => {});
        if (response && response.success && response.filePath) {
          // Use relative path for preview
          setPartnerSignaturePreviews(prev => ({ ...prev, [partnerIndex]: response.filePath }));
          setPartnerSignatureErrors(prev => ({ ...prev, [partnerIndex]: '' }));
          
          // Update partner's signature in formData
          const updatedPartners = [...(formData.partnerDirectorDetails || [])];
          if (updatedPartners[partnerIndex]) {
            updatedPartners[partnerIndex] = { ...updatedPartners[partnerIndex], signatureFile: response.filePath };
            setFormData(prev => ({ ...prev, partnerDirectorDetails: updatedPartners }));
            debouncedSave({ ...formData, partnerDirectorDetails: updatedPartners });
          }
        }
      } catch (error) {
        console.error('Error uploading partner signature file:', error);
        setPartnerSignatureErrors(prev => ({ ...prev, [partnerIndex]: 'Failed to upload signature. Please try again.' }));
      } finally {
        setPartnerSignatureUploading(prev => ({ ...prev, [partnerIndex]: false }));
      }
    } else {
      const previewUrl = URL.createObjectURL(file);
      setPartnerSignaturePreviews(prev => ({ ...prev, [partnerIndex]: previewUrl }));
      setPartnerSignatureErrors(prev => ({ ...prev, [partnerIndex]: '' }));
      setPartnerSignatureUploading(prev => ({ ...prev, [partnerIndex]: false }));
    }
  };

  // Open signature in modal for preview/edit
  const handleSignatureClick = () => {
    if (!signaturePreviewUrl && !formData.signatureFile) return;
    
    let url = signaturePreviewUrl;
    let type = 'image/png';
    
    if (!url && formData.signatureFile) {
      if (typeof formData.signatureFile === 'string') {
        url = formData.signatureFile;
      } else {
        url = URL.createObjectURL(formData.signatureFile);
        type = formData.signatureFile.type;
      }
    }
    
    if (url) {
      setModalFileUrl(url);
      setModalFileName('Signature');
      setModalFileType(type);
      setIsModalOpen(true);
    }
  };

  // Open partner signature in modal
  const handlePartnerSignatureClick = (partnerIndex: number) => {
    const previewUrl = partnerSignaturePreviews[partnerIndex];
    if (!previewUrl) return;
    
    setModalFileUrl(previewUrl);
    setModalFileName(`${(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #${partnerIndex + 2} Signature`);
    setModalFileType('image/png');
    setIsModalOpen(true);
  };

  // Global progress bar (across Page1+2+3+4) — PageA and Page5 are info-only
  const formProgress = useFormProgress(formData);

  // Update signature preview when formData.signatureFile changes (e.g., from loaded draft)
  useEffect(() => {
    if (formData.signatureFile) {
      if (typeof formData.signatureFile === 'string') {
        // Use relative path - browser will resolve it correctly
        // File paths from server start with /uploads/...
        setSignaturePreviewUrl(formData.signatureFile);
      } else if (formData.signatureFile instanceof File && formData.signatureFile.type.startsWith('image/')) {
        const url = URL.createObjectURL(formData.signatureFile);
        setSignaturePreviewUrl(url);
        return () => URL.revokeObjectURL(url);
      }
    } else {
      setSignaturePreviewUrl(null);
    }
  }, [formData.signatureFile]);

  // Update partner signature previews when loading from draft
  useEffect(() => {
    if (formData.partnerDirectorDetails && formData.partnerDirectorDetails.length > 0) {
      const previews: { [key: number]: string } = {};
      
      formData.partnerDirectorDetails.forEach((partner, index) => {
        if (partner.signatureFile && typeof partner.signatureFile === 'string') {
          // Use relative path - browser will resolve it correctly
          previews[index] = partner.signatureFile;
        }
      });
      
      if (Object.keys(previews).length > 0) {
        setPartnerSignaturePreviews(prev => ({ ...prev, ...previews }));
      }
    }
  }, [formData.partnerDirectorDetails]);

  // Check if dealership type is Individual/OPC/Proprietorship (no partners)
  const isIndividualOrProprietorship =
    formData.dealershipType === 'Individual' || formData.dealershipType === 'OPC' || formData.dealershipType === 'Proprietorship';

  // State for PDF generation
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);

  // Effect to initialize or generate applicationId
  useEffect(() => {
    // If formData is passed as a prop, we don't need to generate or fetch an ID
    if (propFormData) {
      if (propFormData.applicationNoOfficeUse) {
        setApplicationId(propFormData.applicationNoOfficeUse);
      }
      return;
    }

    let currentApplicationId = location.state?.applicationId || sessionStorage.getItem('currentApplicationId');
    let source = '';

    if (currentApplicationId) {
      source = location.state?.applicationId ? 'location.state' : 'sessionStorage';
      console.log(`[ApplicationFormPage3 Effect InitID] Found existing Application ID: ${currentApplicationId} from ${source}`);
      setApplicationId(currentApplicationId);
      if (formData.applicationNoOfficeUse !== currentApplicationId) {
        setFormData(prev => ({ ...prev, applicationNoOfficeUse: currentApplicationId }));
      }
    } else {
      const newId = generateApplicationId();
      console.log(`[ApplicationFormPage3 Effect InitID] No existing ID found. Generated new Application ID: ${newId}`);
      setApplicationId(newId);
      sessionStorage.setItem('currentApplicationId', newId);
      isNewlyGeneratedIdRef.current = true;
      setFormData(prev => ({ ...prev, applicationNoOfficeUse: newId }));
    }
  }, [location.state, propFormData]);

  // Effect to load existing application data when resuming a draft
  useEffect(() => {
    const loadDraftData = async () => {
      // If formData is passed as a prop, don't fetch from API
      if (propFormData) {
        console.log('[ApplicationFormPage3 LoadDraft] Using prop formData, skipping draft load.');
        return;
      }

      if (applicationId && !isNewlyGeneratedIdRef.current && !isViewMode) {
        console.log(`[ApplicationFormPage3 LoadDraft] Attempting to load draft data for ID: ${applicationId}`);
        try {
          const appData = await fetchApplicationDetails(applicationId);
          if (appData && appData.formData) {
            // Get today's date in YYYY-MM-DD format for date input
            const today = new Date().toISOString().split('T')[0];
            
            // Auto-fill Place from Page 1's CNG Station Location field
            let autoPlace = appData.formData.declarationPlace || '';
            if (!autoPlace) {
              // Use the CNG Station Location selection from Page 1
              autoPlace = appData.formData.location || '';
            }
            
            const loadedFormData = {
              ...appData.formData,
              // Auto-fill signatureFullName from nameInBlocks if not already set
              signatureFullName: appData.formData.signatureFullName || appData.formData.nameInBlocks || '',
              // Auto-fill DECLARATION fields from Page 1
              declarantName: appData.formData.declarantName || appData.formData.nameInBlocks || '',
              declarantParentName: appData.formData.declarantParentName || appData.formData.fatherOrHusbandName || '',
              // Auto-fill Place from Page 1's location data
              declarationPlace: appData.formData.declarationPlace || autoPlace,
              // Pre-fill Date with current date if not already set
              declarationDate: appData.formData.declarationDate || today,
            };
            setFormData(loadedFormData);
            console.log(`[ApplicationFormPage3 LoadDraft] Successfully loaded draft data for ID: ${applicationId}`, appData);
          } else {
            console.warn(`[ApplicationFormPage3 LoadDraft] No application data or formData found for ID: ${applicationId}`, appData);
          }
        } catch (error) {
          console.error(`[ApplicationFormPage3 LoadDraft] Error loading draft data for ${applicationId}:`, error);
        }
      }
    };

    loadDraftData();
  }, [applicationId, isViewMode, propFormData]);

  // Effect to check if application is locked (after payment or manually locked by admin)
  useEffect(() => {
    const checkLockStatus = async () => {
      if (!applicationId) return;
      try {
        const response = await axios.get(`${API_BASE}/api/settings/application-lock-status/${applicationId}`);
        if (response.data.success && response.data.isLocked) {
          setIsApplicationLocked(true);
          console.log(`[AppFormPage3] Application ${applicationId} is locked: ${response.data.reason}`);
        } else {
          setIsApplicationLocked(false);
        }
      } catch (error) {
        console.error('[AppFormPage3] Error checking application lock status:', error);
      }
    };
    checkLockStatus();
  }, [applicationId]);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleOpenFilePreview = (file: File | string | null, defaultFileName: string = 'file') => {
    console.log('[handleOpenFilePreview] Called with file:', file, 'defaultFileName:', defaultFileName);
    if (!file) {
      console.log('[handleOpenFilePreview] File is null, returning.');
      return;
    }

    console.log('[handleOpenFilePreview] Current API_BASE_URL:', API_BASE_URL);

    let url: string | null = null;
    let name: string | null = null;
    let type: string | null = null;

    if (typeof file === 'string') {
      console.log('[handleOpenFilePreview] File is a string:', file);

      // Determine backend origin consistently using REACT_APP_API_URL if absolute; otherwise fallback to current origin
      const rawApiBaseUrl = process.env.REACT_APP_API_URL || '/api';
      let serverBaseForFiles = window.location.origin;
      if (rawApiBaseUrl.startsWith('http')) {
        try {
          const urlObject = new URL(rawApiBaseUrl);
          serverBaseForFiles = `${urlObject.protocol}//${urlObject.host}`;
        } catch (e) {
          console.warn('[handleOpenFilePreview] Could not parse REACT_APP_API_URL, using window.origin instead:', e);
          serverBaseForFiles = window.location.origin;
        }
      } else {
        // Relative API URL - check if we're in development
        const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (isDevelopment && window.location.port === '3000') {
          serverBaseForFiles = `${window.location.protocol}//${window.location.hostname}:5000`;
        }
      }

      // Normalize to a path
      let normalizedPath = file;
      if (file.includes('://')) {
        try { normalizedPath = new URL(file).pathname; } catch { normalizedPath = file; }
      }
      if (!normalizedPath.startsWith('/')) {
        normalizedPath = normalizedPath.startsWith('uploads/') ? `/${normalizedPath}` : `/uploads/${normalizedPath}`;
      }

      url = `${serverBaseForFiles}${normalizedPath}`;
      name = normalizedPath.split('/').pop() || defaultFileName;

      const lower = name.toLowerCase();
      if (lower.endsWith('.pdf')) {
        type = 'application/pdf';
      } else if (lower.match(/\.(jpeg|jpg|gif|png)$/i)) {
        const ext = lower.split('.').pop();
        type = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      } else {
        type = 'application/octet-stream';
      }
      console.log(`[handleOpenFilePreview] Constructed URL: ${url}, Name: ${name}, Type: ${type}`);
    } else {
      console.log('[handleOpenFilePreview] File is a File object:', file);
      url = URL.createObjectURL(file);
      name = file.name;
      type = file.type;
      console.log('[handleOpenFilePreview] File object - Constructed URL:', url, 'Name:', name, 'Type:', type);
    }

    setModalFileUrl(url);
    setModalFileName(name);
    setModalFileType(type);
    setIsModalOpen(true);
    console.log('[handleOpenFilePreview] Modal state set. isOpen:', true, 'Url:', url);
  };

  const handleDownloadPdf = async () => {
    setIsPdfGenerating(true);
    setCompressionProgress(0);
    const formElement = formRef.current;
    if (!formElement) {
      alert('Form content is not available for PDF export.');
      setIsPdfGenerating(false);
      return;
    }

    const formClone = formElement.cloneNode(true) as HTMLElement;

    try {
      formClone.style.position = 'absolute';
      formClone.style.left = '-9999px';
      formClone.style.top = '0px';
      formClone.style.width = `${formElement.offsetWidth}px`;
      document.body.appendChild(formClone);

      const inputs = formClone.querySelectorAll('input[type="text"], input[type="number"], textarea');
      inputs.forEach(input => {
        const typedInput = input as HTMLInputElement | HTMLTextAreaElement;
        const value = typedInput.value;
        if (value) {
          const span = document.createElement('span');
          span.textContent = value;
          span.style.borderBottom = '1px solid #333';
          span.style.padding = '2px 5px';
          span.style.margin = '0 5px';
          span.style.fontSize = 'inherit';
          span.style.fontFamily = 'inherit';
          span.style.display = 'inline-block';
          span.style.whiteSpace = 'pre-wrap';
          span.style.wordBreak = 'break-word';
          span.style.color = window.getComputedStyle(typedInput).color;
          typedInput.parentNode?.replaceChild(span, typedInput);
        }
      });

      const checkboxes = formClone.querySelectorAll('input[type="checkbox"], input[type="radio"]');
      checkboxes.forEach(checkbox => {
        const typedCheckbox = checkbox as HTMLInputElement;
        const span = document.createElement('span');
        span.textContent = typedCheckbox.checked ? '☑' : '☐';
        span.style.fontSize = 'inherit';
        span.style.fontFamily = 'inherit';
        span.style.display = 'inline-block';
        span.style.margin = '0 5px';
        typedCheckbox.parentNode?.replaceChild(span, typedCheckbox);
      });

      const buttons = formClone.querySelectorAll('button');
      buttons.forEach(btn => (btn.style.visibility = 'hidden'));

      const canvas = await html2canvas(formClone, {
        scale: 2,
        useCORS: true,
        width: formClone.scrollWidth,
        height: formClone.scrollHeight,
        windowWidth: formClone.scrollWidth,
        windowHeight: formClone.scrollHeight,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.5); // Use aggressive JPEG compression
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'l' : 'p',
        unit: 'px',
        format: [canvas.width, canvas.height],
      });

      pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height);
      
      // Simulate compression process for visual feedback
      for (let i = 0; i <= 100; i += 10) {
        setCompressionProgress(i);
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      pdf.save(`application_form_page3_${applicationId || 'draft'}.pdf`);
    } catch (error) {
      console.error('PDF Generation Error:', error);
      alert(`Error generating PDF for Page 3. Please check the console for details. Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      if (formClone.parentNode === document.body) {
        document.body.removeChild(formClone);
      }
      setIsPdfGenerating(false);
      setCompressionProgress(0);
    }
  };

  // Fill dummy data function
  const handleFillDummyData = () => {
    const today = new Date().toISOString().split('T')[0];
    setFormData({
      applicationNoOfficeUse: applicationId,
      declarantName: 'John Doe',
      declarantParentName: 'James Doe',
      signatureFullName: 'JOHN DOE',
      declarationPlace: 'Mumbai',
      declarationDate: today,
      signatureFile: null,
      errors: {}
    });
  };

  // Function to save data to database
  const saveToDatabaseCallback = useCallback(async (data: typeof formData, isDraft: boolean, anId?: string) => {
    const idToUse = anId || applicationId;

    if (!idToUse) {
      console.error('Application ID is missing, cannot save.');
      setSaveMessage('Error: Application ID missing.');
      setIsSaving(false);
      return;
    }

    if (!isOnline) {
      console.warn('Offline: Save operation skipped for application ID:', idToUse);
      setSaveMessage('Offline: Changes will be saved when back online.');
      return;
    }

    console.log(`[SaveToDB] Attempting to save. ID: ${idToUse}, isDraft: ${isDraft}`);
    setIsSaving(true);
    const currentSaveMessage = isDraft ? 'Saving Draft...' : 'Submitting Application...';
    setSaveMessage(currentSaveMessage);
    setShowSaveIndicator(true);
    
    try {
      const dataToSave = {
        ...data,
      };
      
      const response = await saveFormData(idToUse, dataToSave, isDraft);
      
      if (response.success) {
        const successMsg = isDraft ? 'Draft saved successfully' : 'Application submitted successfully';
        console.log(`[SaveToDB] Success for ID: ${idToUse}. Message: ${successMsg}`);
        setSaveMessage(successMsg);
        if (isDraft && isNewlyGeneratedIdRef.current) {
          isNewlyGeneratedIdRef.current = false;
          console.log('[SaveToDB] Reset isNewlyGeneratedIdRef for ID:', idToUse);
        }
        setTimeout(() => setShowSaveIndicator(false), 2000);
      } else {
        setSaveMessage('Error saving');
        setTimeout(() => setShowSaveIndicator(false), 3000);
      }
    } catch (error) {
      console.error('Error saving form data:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setShowSaveIndicator(false), 3000);
    } finally {
      setIsSaving(false);
    }
  }, [applicationId, isOnline]);

  // Effect for initial auto-save of a newly generated ID
  useEffect(() => {
    if (applicationId && isNewlyGeneratedIdRef.current && isOnline && Object.keys(formData).some(key => formData[key as keyof FormData] !== '' && key !== 'errors' && key !== 'applicationNoOfficeUse')) {
      console.log(`[ApplicationFormPage3 Effect AutoSave] Application ID ${applicationId} is newly generated. Attempting to save as draft.`);
      saveToDatabaseCallback(formData, true, applicationId);
    }
  }, [applicationId, formData, saveToDatabaseCallback, isOnline]);

  const debouncedSave = useCallback((newData: FormData) => {
    console.log('[DebouncedSave] Invoked. Clearing previous timeout if any.');
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      console.log('[DebouncedSave] Timeout executed. Checking conditions to save.');
      if (applicationId) {
        console.log(`[DebouncedSave] Conditions met. ApplicationId: ${applicationId}. Proceeding to save.`);
        console.log(`[DebouncedSave] Triggering auto-save (as draft) for ID: ${applicationId}`);
        saveToDatabaseCallback({ ...newData, applicationNoOfficeUse: applicationId }, true, applicationId);
      } else {
        console.log(`[DebouncedSave] Conditions NOT met. Skipping save. ApplicationId: ${applicationId}`);
      }
    }, 1000);
  }, [saveToDatabaseCallback, applicationId]);

  // Removed interval-based auto-save - using navigation-based save modal instead

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    // Removed auto-save on every keystroke - use SAVE DRAFT button or navigation modal instead
  };

  const handleZoomIn = () => {
    setZoomScale(prev => Math.min(prev + 0.1, 1.5));
  };
  
  const handleZoomOut = () => {
    setZoomScale(prev => Math.max(prev - 0.1, 0.6));
  };

  // Validate signature is uploaded before navigation
  const validateMandatoryFields = (): boolean => {
    // If a partner/director is editing (not main applicant), validate their signature
    if (typeof editingUser === 'number') {
      const partnerIndex = editingUser;
      const partner = formData.partnerDirectorDetails?.[partnerIndex];
      if (!partner?.signatureFile) {
        // Find and shake the partner's signature section
        const partnerSignatureElement = document.getElementById(`partnerSignatureFile_${partnerIndex}`);
        if (partnerSignatureElement) {
          const parentRow = partnerSignatureElement.closest('tr');
          if (parentRow) {
            parentRow.style.animation = 'shake 0.5s ease-in-out';
            setTimeout(() => { parentRow.style.animation = ''; }, 500);
            parentRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        return false;
      }
      return true;
    }
    
    // For main applicant, validate their signature
    if (!formData.signatureFile) {
      // Trigger shake animation
      setIsSignatureShaking(true);
      setTimeout(() => setIsSignatureShaking(false), 500);
      signatureRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    return true;
  };

  const handleNextButtonClick = () => {
    // Only validate for partners - main applicant can navigate freely
    // Payment button will be greyed out on Page 5 if partners incomplete
    if (typeof editingUser === 'number') {
      if (!validateMandatoryFields()) {
        return;
      }
    }
    
    // Show save modal before navigation (skip if application is locked)
    if (applicationId && isOnline && !isApplicationLocked) {
      setPendingNavigation('/form/pageA');
      setShowSaveModal(true);
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate('/form/pageA', { state: { applicationId } });
    }
  };

  // Handle save modal actions
  const handleSaveAndNavigate = async () => {
    setShowSaveModal(false);
    
    if (applicationId && isOnline) {
      console.log('[Navigation Page3] Saving draft before navigating');
      await saveToDatabaseCallback(formData, true, applicationId);
      
      // Wait 1.5 seconds so user can see the "Draft saved successfully" message
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
    
    if (pendingNavigation) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(pendingNavigation, { state: { applicationId } });
      setPendingNavigation(null);
    }
  };

  const handleSkipSaveAndNavigate = () => {
    setShowSaveModal(false);
    if (pendingNavigation) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(pendingNavigation, { state: { applicationId } });
      setPendingNavigation(null);
    }
  };

  // Handle navigation from vertical nav bar - show save modal
  const handleNavBarNavigation = (targetPath: string) => {
    // Only validate for partners navigating forward (to pageA, page4, page5, etc.)
    // Main applicant can navigate freely - payment button will be greyed out if partners incomplete
    if (typeof editingUser === 'number') {
      const isForwardNavigation = targetPath.includes('pageA') || targetPath.includes('page4') || 
        targetPath.includes('page5') || targetPath.includes('page14') ||
        parseInt(targetPath.replace(/\D/g, '')) > 3;
      
      if (isForwardNavigation && !validateMandatoryFields()) {
        return;
      }
    }
    
    // Skip save modal if application is locked
    if (applicationId && isOnline && !isApplicationLocked) {
      setPendingNavigation(targetPath);
      setShowSaveModal(true);
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(targetPath, { state: { applicationId } });
    }
  };

  // Form progress is now calculated by the shared useFormProgress hook (global across Page1+2+3)

  // Effect for online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <>
      {/* Global shake animation keyframes */}
      <GlobalShakeStyle />
      
      {/* Progress bar */}
      <ProgressBarContainer>
        <ProgressBarFill progress={formProgress} />
      </ProgressBarContainer>
      <ProgressLabel>Page 3/5 • {Math.round(formProgress)}% complete</ProgressLabel>
      
      {/* Zoom controls */}
      <ZoomControlsContainer>
        <ZoomButton onClick={handleZoomIn}>+</ZoomButton>
        <ZoomButton onClick={handleZoomOut}>−</ZoomButton>
      </ZoomControlsContainer>
      
      {/* Save indicator */}
      <SaveIndicator visible={showSaveIndicator}>
        {saveMessage}
      </SaveIndicator>

      {!isOnline && (
        <OfflineIndicator>
          You are currently offline. Changes will not be saved until you reconnect.
        </OfflineIndicator>
      )}
      {/* Application Locked Banner */}
      {isApplicationLocked && !isViewMode && (
        <div style={{
          position: 'fixed',
          top: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(254, 243, 199, 0.92)',
          backdropFilter: 'blur(8px)',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '10px 20px',
          zIndex: 9998,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 4px 20px rgba(245, 158, 11, 0.25)',
          maxWidth: '90%'
        }}>
          <span style={{ fontSize: '1.3rem' }}>🔒</span>
          <div>
            <div style={{ fontWeight: 600, color: '#92400e', fontSize: '0.9rem' }}>
              Application Locked
            </div>
            <div style={{ color: '#78350f', fontSize: '0.75rem' }}>
              This application is locked. You can view data but cannot make changes.
            </div>
          </div>
        </div>
      )}
      
      <FloatingBackButton onClick={() => { window.scrollTo({ top: 0, behavior: 'auto' }); navigate('/form/page2'); }} aria-label="Back to previous page">
        ←
      </FloatingBackButton>
      <div style={{ position: 'fixed', top: '80px', left: '30px', zIndex: 1001 }}>
        <button 
          onClick={() => setShowBrochureModal(true)}
          style={{ 
            backgroundColor: '#2196f3', 
            color: '#ffffff', 
            borderRadius: '4px', 
            padding: '8px 15px',
            border: '1px solid #1976d2',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          📄 View Brochure
        </button>
      </div>
      
      {showDummyDataButton && (
        <div style={{ position: 'fixed', top: '80px', right: '30px', zIndex: 1001, display: 'block' }}>
          <button 
            onClick={handleFillDummyData} 
            style={{ 
              backgroundColor: '#ffc107', 
              color: '#212529', 
              borderRadius: '4px', 
              padding: '8px 15px',
              border: '1px solid #e0a800',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }}
          >
            Fill Dummy Data
          </button>
        </div>
      )}
      
      <FormPageNavigator currentPage={3} totalPages={14} targetFormRef={formRef} zoomScale={zoomScale} onNavigate={handleNavBarNavigation} />
      
      <div style={{ position: 'fixed', right: '-25px', top: '605px', zIndex: 999 }}>
        <NextPageButton 
          onClick={handleNextButtonClick}
          style={{ 
            height: '35px', 
            width: '140px', 
            borderRadius: '4px', 
            whiteSpace: 'nowrap',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '0 10px'
          }}
        >
          Next Page A →
        </NextPageButton>
      </div>
      {/* WhatsApp Help Button */}
      <div style={{ position: 'fixed', right: '20px', bottom: '100px', zIndex: 999, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#25D366', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>Need Help!</span>
        <a 
          href="https://web.whatsapp.com/send?phone=919971874909&text=Hi%2C%20Need%20Help%20filling%20MNGL%20DODO%20form"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'block', cursor: 'pointer', transition: 'transform 0.2s ease' }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          title="Chat with us on WhatsApp"
        >
          <img src="/assets/images/whatsapp.png" alt="WhatsApp Help" style={{ }} />
        </a>
      </div>
      
      <PageWrapper scale={zoomScale}>
        <A4PageContainer ref={formRef}>
          <FormContentContainer>
            {/* UNDERTAKING Section */}
            <FormFieldFullWidth style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                {formData.nameInBlocks && (
                  <div style={{ fontWeight: 700, color: '#1a5276', fontSize: '1.1em', marginBottom: '8px' }}>
                    {isIndividualOrProprietorship ? (
                      <span style={{ fontSize: '0.9em', color: '#2c3e50', fontWeight: 500, background: 'linear-gradient(135deg, #e8f4fd, #d4edfc)', padding: '4px 12px', borderRadius: '15px', display: 'inline-block' }}>
                        {formData.nameInBlocks}
                      </span>
                    ) : (
                      `${(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #1 (${formData.nameInBlocks})`
                    )}
                  </div>
                )}
                <Label style={{ fontWeight: 700, color: '#1a5276', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline', margin: 0 }}>
                  UNDERTAKING
                  {isMainApplicantSectionLocked && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px', textDecoration: 'none' }}>🔒 Disabled</span>}
                </Label>
              </div>
              <div style={{ fontSize: '0.95em', lineHeight: '1.8', textAlign: 'justify', color: '#333' }}>
                <p style={{ marginBottom: '15px' }}>
                  <strong>a)</strong> I/We have gone through the Information Brochure and all the details available on MNGL website "<a href="https://www.mngl.in" target="_blank" rel="noopener noreferrer" style={{ color: '#1a5276' }}>www.mngl.in</a>" and agree to abide by the same and understand the applicability of the same on me/us.
                </p>
                <p style={{ marginBottom: '15px' }}>
                  <strong>b)</strong> That I/We will arrange funds of approx. Rs 1.5 – 1.8 Crore (excluding land) upon selection, required for development of infrastructure for CNG Station depending upon the type of CNG Station. The Financial Closure for the above-mentioned funds will be made available by me/us within 6 months from the date of issue of LOI.
                </p>
                <p style={{ marginBottom: '15px' }}>
                  <strong>c)</strong> I/We confirm that I/We am/are in possession of the supporting documents in original in respect of the information given by me/us in this application and if selected, failure to present these documents in original will result in Cancellation of selection.
                </p>
                <p style={{ marginBottom: '15px' }}>
                  <strong>d)</strong> I/We am/are fully aware that if I/We am/are unable to obtain Statutory Approvals and Permissions against the site mentioned above due to size of offered land or any other reason, for setting up of DODO CNG Station, then the application made by me/us shall stand cancelled and Security Deposit shall be forfeited.
                </p>
                <p style={{ marginBottom: '0' }}>
                  <strong>e)</strong> That I/We do not fall under the defined "Disqualification Criteria" as mentioned in the Information Brochure.
                </p>
                <p style={{ marginBottom: '0', marginTop: '15px' }}>
                  <strong>f)</strong> I/We fully understand that in case the application submitted by me/us does not qualify to a LOI for reasons not attributable to me/us, then MNGL will refund the SD amount, if paid by me/us, without any interest thereat. I/We fully understand that such decision shall be at sole discretion of MNGL and in such case MNGL shall not be responsible for any losses incurred or suffered by me/us, and I/We hereby agree to indemnify MNGL from any direct or consequential damages thereto, from me/us or any third party, with respect to this transaction.
                </p>
              </div>
            </FormFieldFullWidth>

            {/* DECLARATION Section */}
            <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                {formData.nameInBlocks && (
                  <div style={{ fontWeight: 700, color: '#1a5276', fontSize: '1.1em', marginBottom: '8px' }}>
                    {isIndividualOrProprietorship ? (
                      <span style={{ fontSize: '0.9em', color: '#2c3e50', fontWeight: 500, background: 'linear-gradient(135deg, #e8f4fd, #d4edfc)', padding: '4px 12px', borderRadius: '15px', display: 'inline-block' }}>
                        {formData.nameInBlocks}
                      </span>
                    ) : (
                      `${(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #1 (${formData.nameInBlocks})`
                    )}
                  </div>
                )}
                <Label style={{ fontWeight: 700, color: '#1a5276', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline', margin: 0 }}>
                  DECLARATION
                  {isMainApplicantSectionLocked && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px', textDecoration: 'none' }}>🔒 Disabled</span>}
                </Label>
              </div>
              <div style={{ fontSize: '0.95em', lineHeight: '1.8', textAlign: 'justify', color: '#333' }}>
                <p style={{ marginBottom: '15px' }}>
                  I/We, <StyledInput type="text" name="declarantName" value={formData.declarantName || ''} onChange={handleChange} placeholder="Full Name" disabled={!isOnline || isViewMode || isApplicationLocked || isMainFieldDisabled} style={{ margin: '0 5px', width: '200px', minWidth: '180px', display: 'inline-block', fontWeight: 500, opacity: isMainFieldDisabled ? 0.6 : 1 }} /> 
                  S/O / D/O <StyledInput type="text" name="declarantParentName" value={formData.declarantParentName || ''} onChange={handleChange} placeholder="Father's/Parent's Name" disabled={!isOnline || isViewMode || isApplicationLocked || isMainFieldDisabled} style={{ margin: '0 5px', width: '200px', minWidth: '180px', display: 'inline-block', fontWeight: 500, opacity: isMainFieldDisabled ? 0.6 : 1 }} /> 
                  hereby certify that the information given above is true to the best of my/of belief. Any wrong information / suppression of facts will disqualify me from being considered for this DODO CNG Station Dealership. That if any information/declaration given by me in my application or in any document submitted by me in support of application for the award of the DODO CNG Station dealership shall found to be untrue or incorrect or false, MNGL would be within its rights to forfeit the SD and withdraw the Letter of Intent (LOI), Letter of Acceptance (LOA) or cancel the Dealership (if appointed) and that I would have no claim, whatsoever, against the Corporation for such forfeiture and withdrawal / termination.
                </p>
                <p style={{ fontSize: '0.85em', color: '#666', fontStyle: 'italic', marginTop: '15px' }}>
                  Note: Separate sheets may be used for providing relevant information.
                </p>
              </div>
            </FormFieldFullWidth>

            {/* SIGNATURE Section */}
            <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                {formData.nameInBlocks && (
                  <div style={{ fontWeight: 700, color: '#1a5276', fontSize: '1.1em', marginBottom: '8px' }}>
                    {isIndividualOrProprietorship ? (
                      <span style={{ fontSize: '0.9em', color: '#2c3e50', fontWeight: 500, background: 'linear-gradient(135deg, #e8f4fd, #d4edfc)', padding: '4px 12px', borderRadius: '15px', display: 'inline-block' }}>
                        {formData.nameInBlocks}
                      </span>
                    ) : (
                      `${(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #1 (${formData.nameInBlocks})`
                    )}
                  </div>
                )}
                <Label style={{ fontWeight: 700, color: '#1a5276', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.05em', textDecoration: 'underline', margin: 0 }}>
                  SIGNATURE
                  {isMainApplicantSectionLocked && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px', textDecoration: 'none' }}>🔒 Disabled</span>}
                </Label>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '30%' }}>Full Name</td>
                    <td style={{ padding: '12px', border: '1px solid #85c1e9' }}>
                      <StyledInput 
                        type="text" 
                        name="signatureFullName" 
                        value={formData.signatureFullName || ''} 
                        onChange={(e) => setFormData(prev => ({ ...prev, signatureFullName: e.target.value.toUpperCase() }))} 
                        placeholder="Name in block letters" 
                        disabled={!isOnline || isViewMode || isApplicationLocked || isMainFieldDisabled} 
                        style={{ margin: 0, textTransform: 'uppercase', minWidth: '300px', width: '100%', opacity: isMainFieldDisabled ? 0.6 : 1 }} 
                      />
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '35%' }}>Place</td>
                    <td style={{ padding: '12px', border: '1px solid #85c1e9' }}>
                      <StyledInput type="text" name="declarationPlace" value={formData.declarationPlace || ''} onChange={handleChange} placeholder="To be filled by applicant" disabled={!isOnline || isViewMode || isApplicationLocked || isMainFieldDisabled} style={{ margin: 0, opacity: isMainFieldDisabled ? 0.6 : 1 }} />
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>Date</td>
                    <td style={{ padding: '12px', border: '1px solid #85c1e9' }}>
                      <StyledInput type="date" name="declarationDate" value={formData.declarationDate || ''} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked || isMainFieldDisabled} style={{ margin: 0, maxWidth: '200px', opacity: isMainFieldDisabled ? 0.6 : 1 }} />
                    </td>
                  </tr>
                  <tr ref={signatureRowRef}>
                    <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        Signature of Applicant
                        {!formData.signatureFile && (
                          <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isSignatureShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                        {/* Left side - Upload button and text */}
                        <div style={{ flex: '1' }}>
                          <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>JPG/PNG only (max 5MB) - No PDF</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <FileUploadButton onClick={() => document.getElementById('signatureFile')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isValidatingSignature || isMainFieldDisabled} style={{ opacity: isMainFieldDisabled ? 0.6 : 1, cursor: isMainFieldDisabled ? 'not-allowed' : 'pointer' }}>
                              {isValidatingSignature ? 'Validating...' : formData.signatureFile ? 'Change Signature' : 'Upload Signature'}
                            </FileUploadButton>
                            <input type="file" id="signatureFile" accept=".jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleSignatureUpload} disabled={!isOnline || isViewMode || isApplicationLocked || isMainFieldDisabled} />
                          </div>
                          
                          {/* Validation Error with optional Background Removal */}
                          {signatureValidationError && (
                            <div style={{ 
                              marginTop: '8px', 
                              padding: '10px 12px', 
                              backgroundColor: '#fef2f2', 
                              border: '1px solid #fecaca', 
                              borderRadius: '6px',
                              color: '#dc2626',
                              fontSize: '0.85em'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                <span style={{ fontSize: '16px' }}>⚠️</span>
                                <span>{signatureValidationError}</span>
                              </div>
                              {showBgRemovalOption && (
                                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #fecaca' }}>
                                  <button
                                    onClick={handleRemoveBackground}
                                    disabled={isValidatingSignature}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: '#3b82f6',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '0.85em',
                                      cursor: isValidatingSignature ? 'wait' : 'pointer',
                                      opacity: isValidatingSignature ? 0.7 : 1
                                    }}
                                  >
                                    {isValidatingSignature ? 'Processing...' : '🔧 Try Auto-Remove Background'}
                                  </button>
                                  <span style={{ marginLeft: '8px', fontSize: '0.8em', color: '#666' }}>
                                    (Removes white/light background)
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Right side - Signature Preview Box (2x wider) */}
                        <div 
                          onClick={signaturePreviewUrl ? handleSignatureClick : undefined}
                          style={{ 
                            width: '200px', 
                            height: '70px', 
                            border: signaturePreviewUrl ? '2px solid #3b82f6' : '2px dashed #cbd5e1',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            cursor: signaturePreviewUrl ? 'pointer' : 'default',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fff',
                            boxShadow: signaturePreviewUrl ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s ease',
                            flexShrink: 0
                          }}
                          onMouseEnter={(e) => {
                            if (signaturePreviewUrl) {
                              e.currentTarget.style.transform = 'scale(1.02)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(59,130,246,0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = signaturePreviewUrl ? '0 2px 4px rgba(0,0,0,0.1)' : 'none';
                          }}
                          title={signaturePreviewUrl ? "Click to view/edit signature" : "Signature preview will appear here"}
                        >
                          {signaturePreviewUrl ? (
                            <img 
                              src={signaturePreviewUrl} 
                              alt="Signature" 
                              style={{ 
                                maxWidth: '100%', 
                                maxHeight: '100%', 
                                objectFit: 'contain' 
                              }} 
                            />
                          ) : (
                            <span style={{ fontSize: '0.75em', color: '#94a3b8', textAlign: 'center' }}>Sign Preview</span>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </FormFieldFullWidth>

            {/* Additional Partners/Directors Sections - Only show for Partnership/Pvt Ltd/Public Ltd/Non-Individual types */}
            {!isIndividualOrProprietorship && formData.partnerDirectorDetails && formData.partnerDirectorDetails.length > 0 && formData.partnerDirectorDetails.map((partner, index) => {
              // Alternating color schemes for partners - warm tones for great UX
              const colorSchemes = [
                { bg: '#fef3e2', border: '#fed7aa', headerBg: '#ffedd5', headerBorder: '#fdba74', accent: '#ea580c' }, // Orange
                { bg: '#f0fdf4', border: '#bbf7d0', headerBg: '#dcfce7', headerBorder: '#86efac', accent: '#16a34a' }, // Green
                { bg: '#fdf4ff', border: '#f5d0fe', headerBg: '#fae8ff', headerBorder: '#e879f9', accent: '#a855f7' }, // Purple
                { bg: '#fff7ed', border: '#fed7aa', headerBg: '#ffedd5', headerBorder: '#fdba74', accent: '#c2410c' }, // Deep Orange
              ];
              const scheme = colorSchemes[index % colorSchemes.length];
              
              return (
              <React.Fragment key={partner.id || index}>
                {/* Partner UNDERTAKING Section */}
                <FormFieldFullWidth style={{ marginTop: '30px', padding: '20px', backgroundColor: scheme.bg, borderRadius: '12px', border: `2px solid ${scheme.border}`, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <div style={{ fontWeight: 700, color: scheme.accent, fontSize: '1.15em', marginBottom: '8px', padding: '8px 16px', backgroundColor: scheme.headerBg, borderRadius: '8px', display: 'inline-block', border: `1px solid ${scheme.headerBorder}` }}>
                      {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #{index + 2} ({partner.nameInBlocks || 'Name not provided'})
                    </div>
                    <Label style={{ fontWeight: 700, color: '#1a5276', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline', margin: '12px 0 0 0' }}>
                      UNDERTAKING
                      {isPartnerSectionLocked(index) && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px', textDecoration: 'none' }}>🔒 Disabled</span>}
                    </Label>
                  </div>
                  <div style={{ fontSize: '0.95em', lineHeight: '1.8', textAlign: 'justify', color: '#333' }}>
                    <p style={{ marginBottom: '15px' }}>
                      <strong>a)</strong> I/We have gone through the Information Brochure and all the details available on MNGL website "<a href="https://www.mngl.in" target="_blank" rel="noopener noreferrer" style={{ color: '#1a5276' }}>www.mngl.in</a>" and agree to abide by the same and understand the applicability of the same on me/us.
                    </p>
                    <p style={{ marginBottom: '15px' }}>
                      <strong>b)</strong> That I/We will arrange funds of approx. Rs 1.5 – 1.8 Crore (excluding land) upon selection, required for development of infrastructure for CNG Station depending upon the type of CNG Station. The Financial Closure for the above-mentioned funds will be made available by me/us within 6 months from the date of issue of LOI.
                    </p>
                    <p style={{ marginBottom: '15px' }}>
                      <strong>c)</strong> I/We confirm that I/We am/are in possession of the supporting documents in original in respect of the information given by me/us in this application and if selected, failure to present these documents in original will result in Cancellation of selection.
                    </p>
                    <p style={{ marginBottom: '15px' }}>
                      <strong>d)</strong> I/We am/are fully aware that if I/We am/are unable to obtain Statutory Approvals and Permissions against the site mentioned above due to size of offered land or any other reason, for setting up of DODO CNG Station, then the application made by me/us shall stand cancelled and Security Deposit shall be forfeited.
                    </p>
                    <p style={{ marginBottom: '0' }}>
                      <strong>e)</strong> That I/We do not fall under the defined "Disqualification Criteria" as mentioned in the Information Brochure.
                    </p>
                    <p style={{ marginBottom: '0', marginTop: '15px' }}>
                      <strong>f)</strong> I/We fully understand that in case the application submitted by me/us does not qualify to a LOI for reasons not attributable to me/us, then MNGL will refund the SD amount, if paid by me/us, without any interest thereat. I/We fully understand that such decision shall be at sole discretion of MNGL and in such case MNGL shall not be responsible for any losses incurred or suffered by me/us, and I/We hereby agree to indemnify MNGL from any direct or consequential damages thereto, from me/us or any third party, with respect to this transaction.
                    </p>
                  </div>
                </FormFieldFullWidth>

                {/* Partner DECLARATION Section */}
                <FormFieldFullWidth style={{ marginTop: '20px', padding: '20px', backgroundColor: scheme.bg, borderRadius: '12px', border: `2px solid ${scheme.border}`, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <div style={{ fontWeight: 700, color: scheme.accent, fontSize: '1.15em', marginBottom: '8px', padding: '8px 16px', backgroundColor: scheme.headerBg, borderRadius: '8px', display: 'inline-block', border: `1px solid ${scheme.headerBorder}` }}>
                      {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #{index + 2} ({partner.nameInBlocks || 'Name not provided'})
                    </div>
                    <Label style={{ fontWeight: 700, color: '#1a5276', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline', margin: '12px 0 0 0' }}>
                      DECLARATION
                      {isPartnerSectionLocked(index) && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px', textDecoration: 'none' }}>🔒 Disabled</span>}
                    </Label>
                  </div>
                  <div style={{ fontSize: '0.95em', lineHeight: '1.8', textAlign: 'justify', color: '#333' }}>
                    <p style={{ marginBottom: '15px' }}>
                      I/We, <strong>{partner.nameInBlocks || '_______________'}</strong> S/O / D/O <strong>{partner.fatherOrHusbandName || '_______________'}</strong> hereby certify that the information given above is true to the best of my/of belief. Any wrong information / suppression of facts will disqualify me from being considered for this DODO CNG Station Dealership. That if any information/declaration given by me in my application or in any document submitted by me in support of application for the award of the DODO CNG Station dealership shall found to be untrue or incorrect or false, MNGL would be within its rights to forfeit the SD and withdraw the Letter of Intent (LOI), Letter of Acceptance (LOA) or cancel the Dealership (if appointed) and that I would have no claim, whatsoever, against the Corporation for such forfeiture and withdrawal / termination.
                    </p>
                    <p style={{ fontSize: '0.85em', color: '#666', fontStyle: 'italic', marginTop: '15px' }}>
                      Note: Separate sheets may be used for providing relevant information.
                    </p>
                  </div>
                </FormFieldFullWidth>

                {/* Partner SIGNATURE Section */}
                <FormFieldFullWidth style={{ marginTop: '20px', padding: '20px', backgroundColor: scheme.bg, borderRadius: '12px', border: `2px solid ${scheme.border}`, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <div style={{ fontWeight: 700, color: scheme.accent, fontSize: '1.15em', marginBottom: '8px', padding: '8px 16px', backgroundColor: scheme.headerBg, borderRadius: '8px', display: 'inline-block', border: `1px solid ${scheme.headerBorder}` }}>
                      {(formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director'} #{index + 2} ({partner.nameInBlocks || 'Name not provided'})
                    </div>
                    <Label style={{ fontWeight: 700, color: '#1a5276', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.05em', textDecoration: 'underline', margin: '12px 0 0 0' }}>
                      SIGNATURE
                      {isPartnerSectionLocked(index) && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px', textDecoration: 'none' }}>🔒 Disabled</span>}
                    </Label>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '12px', border: `1px solid ${scheme.border}`, backgroundColor: scheme.headerBg, fontWeight: 600, width: '35%' }}>Full Name</td>
                        <td style={{ padding: '12px', border: `1px solid ${scheme.border}` }}>
                          <span style={{ fontWeight: 500, textTransform: 'uppercase' }}>{partner.nameInBlocks || 'Not provided'}</span>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '12px', border: `1px solid ${scheme.border}`, backgroundColor: scheme.headerBg, fontWeight: 600, width: '35%' }}>Place</td>
                        <td style={{ padding: '12px', border: `1px solid ${scheme.border}` }}>
                          <StyledInput type="text" name={`partner_${index}_place`} value={formData.declarationPlace || ''} onChange={handleChange} placeholder="Enter place" disabled={!isOnline || isViewMode || isApplicationLocked || isPartnerSectionLocked(index)} style={{ margin: 0, opacity: isPartnerSectionLocked(index) ? 0.6 : 1 }} />
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '12px', border: `1px solid ${scheme.border}`, backgroundColor: scheme.headerBg, fontWeight: 600 }}>Date</td>
                        <td style={{ padding: '12px', border: `1px solid ${scheme.border}` }}>
                          <StyledInput type="date" name={`partner_${index}_date`} value={formData.declarationDate || ''} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked || isPartnerSectionLocked(index)} style={{ margin: 0, maxWidth: '200px', opacity: isPartnerSectionLocked(index) ? 0.6 : 1 }} />
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '12px', border: `1px solid ${scheme.border}`, backgroundColor: scheme.headerBg, fontWeight: 600 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            Signature of Partner/Director
                            {!partner.signatureFile && (
                              <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px', border: `1px solid ${scheme.border}` }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                            {/* Left side - Upload button and text */}
                            <div style={{ flex: '1' }}>
                              <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>JPG/PNG only (max 5MB) - No PDF</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <FileUploadButton 
                                  onClick={() => document.getElementById(`partnerSignatureFile_${index}`)?.click()} 
                                  disabled={!isOnline || isViewMode || isApplicationLocked || partnerSignatureUploading[index] || isPartnerSectionLocked(index)}
                                  style={{ opacity: isPartnerSectionLocked(index) ? 0.6 : 1, cursor: isPartnerSectionLocked(index) ? 'not-allowed' : 'pointer' }}
                                >
                                  {partnerSignatureUploading[index] ? 'Uploading...' : partnerSignaturePreviews[index] ? 'Change Signature' : 'Upload Signature'}
                                </FileUploadButton>
                                <input 
                                  type="file" 
                                  id={`partnerSignatureFile_${index}`} 
                                  accept=".jpg,.jpeg,.png" 
                                  style={{ display: 'none' }} 
                                  onChange={(e) => handlePartnerSignatureUpload(e, index)}
                                  disabled={!isOnline || isViewMode || isApplicationLocked || isPartnerSectionLocked(index)} 
                                />
                              </div>
                              
                              {/* Partner Validation Error */}
                              {partnerSignatureErrors[index] && (
                                <div style={{ 
                                  marginTop: '8px', 
                                  padding: '8px 12px', 
                                  backgroundColor: '#fef2f2', 
                                  border: '1px solid #fecaca', 
                                  borderRadius: '6px',
                                  color: '#dc2626',
                                  fontSize: '0.85em',
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '8px'
                                }}>
                                  <span style={{ fontSize: '16px' }}>⚠️</span>
                                  <span>{partnerSignatureErrors[index]}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Right side - Partner Signature Preview Box (same size as main applicant: 200x70) */}
                            <div 
                              onClick={partnerSignaturePreviews[index] ? () => handlePartnerSignatureClick(index) : undefined}
                              style={{ 
                                width: '200px', 
                                height: '70px', 
                                border: partnerSignaturePreviews[index] ? `2px solid ${scheme.accent}` : '2px dashed #cbd5e1',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                cursor: partnerSignaturePreviews[index] ? 'pointer' : 'default',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#fff',
                                boxShadow: partnerSignaturePreviews[index] ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                                transition: 'all 0.2s ease',
                                flexShrink: 0,
                                position: 'relative'
                              }}
                              onMouseEnter={(e) => {
                                if (partnerSignaturePreviews[index]) {
                                  e.currentTarget.style.transform = 'scale(1.02)';
                                  e.currentTarget.style.boxShadow = `0 4px 8px ${scheme.accent}40`;
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = partnerSignaturePreviews[index] ? '0 2px 4px rgba(0,0,0,0.1)' : 'none';
                              }}
                              title={partnerSignaturePreviews[index] ? "Click to view/edit signature" : "Signature preview will appear here"}
                            >
                              {partnerSignatureUploading[index] ? (
                                /* Upload Animation */
                                <div style={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  alignItems: 'center', 
                                  gap: '8px' 
                                }}>
                                  <div style={{
                                    width: '24px',
                                    height: '24px',
                                    border: `3px solid ${scheme.border}`,
                                    borderTop: `3px solid ${scheme.accent}`,
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                  }} />
                                  <span style={{ fontSize: '0.7em', color: scheme.accent, fontWeight: 500 }}>Uploading...</span>
                                  <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                                </div>
                              ) : partnerSignaturePreviews[index] ? (
                                <img 
                                  src={partnerSignaturePreviews[index]} 
                                  alt={`Partner ${index + 2} Signature`} 
                                  style={{ 
                                    maxWidth: '100%', 
                                    maxHeight: '100%', 
                                    objectFit: 'contain' 
                                  }} 
                                />
                              ) : (
                                <span style={{ fontSize: '0.75em', color: '#94a3b8', textAlign: 'center' }}>Sign Preview</span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </FormFieldFullWidth>
              </React.Fragment>
            );
            })}
          </FormContentContainer>
          <PageNumber>3</PageNumber>
        </A4PageContainer>
        
        <FilePreviewModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          fileUrl={modalFileUrl}
          fileName={modalFileName}
          fileType={modalFileType}
        />
      </PageWrapper>
      
      {/* Empty ActionButtonsContainer to maintain structure */}
      <ActionButtonsContainer style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '800px', zIndex: 1000 }}>
        {/* Next Page button moved near FormPageNavigator */}
      </ActionButtonsContainer>
      
      {/* Session Timer */}
      {!isViewMode && (
        <SessionTimer 
          sessionDuration={30}
          warningTime={5}
          onSessionExpired={() => navigate('/')}
          onSaveState={async () => {
            if (applicationId) {
              await saveToDatabaseCallback(formData, true, applicationId);
            }
          }}
        />
      )}

      {/* Application ID Display */}
      {applicationId && <ApplicationIdDisplay applicationId={applicationId} />}

      {/* Home Icon Button - Above Save Icon */}
      <button
        onClick={() => {
          // Skip save modal if application is locked
          if (applicationId && isOnline && !isApplicationLocked) {
            setPendingNavigation('/user-dashboard');
            setShowSaveModal(true);
          } else {
            navigate('/user-dashboard');
          }
        }}
        title="Go to Dashboard"
        style={{
          position: 'fixed',
          left: '20px',
          bottom: '150px',
          zIndex: 1000,
          height: '44px',
          padding: '0 16px 0 12px',
          borderRadius: '22px',
          border: 'none',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
          transition: 'all 0.25s ease'
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
        Home
      </button>

      {/* Save Icon Button - Above Back Button - Hidden when application is locked */}
      {!isViewMode && !isApplicationLocked && (
        <button
          onClick={() => saveToDatabaseCallback(formData, true, applicationId)}
          disabled={isSaving || !applicationId || !isOnline}
          title="Save Draft"
          style={{
            position: 'fixed',
            left: '20px',
            bottom: '98px',
            zIndex: 1000,
            height: '44px',
            padding: '0 16px 0 12px',
            borderRadius: '22px',
            border: 'none',
            background: isSaving ? '#86efac' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            color: 'white',
            cursor: isSaving || !applicationId || !isOnline ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: 600,
            boxShadow: '0 4px 14px rgba(34, 197, 94, 0.35)',
            transition: 'all 0.25s ease',
            opacity: isSaving || !applicationId || !isOnline ? 0.6 : 1
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
            <polyline points="17 21 17 13 7 13 7 21"/>
            <polyline points="7 3 7 8 15 8"/>
          </svg>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      )}
      
      {isViewMode && (
        <PayNowButton 
          onClick={handleDownloadPdf} 
          disabled={!isOnline}
          style={{ position: 'fixed', left: '95px', bottom: '31.5px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center', fontWeight: 'bold', letterSpacing: '0.2px' }}
        > 
          DOWNLOAD PDF
        </PayNowButton>
      )}
      
      {isPdfGenerating && (
        <ModalOverlay>
          <ModalContent>
            <h3>Generating PDF</h3>
            <p>Please wait... Generating PDF</p>
            <ProgressBarContainerModal>
              <ProgressBarFillModal progress={compressionProgress} />
            </ProgressBarContainerModal>
            <ProgressText>Compression: {compressionProgress}%</ProgressText>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Save Progress Modal */}
      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2200,
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '28px 32px',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            maxWidth: '380px',
            width: '90%',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2.5em', marginBottom: '12px' }}>💾</div>
            <h3 style={{ margin: '0 0 10px 0', color: '#2c3e50', fontSize: '1.15em', fontWeight: 600 }}>
              Save your progress?
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '0.9em', color: '#666', lineHeight: 1.5 }}>
              Would you like to save your form before continuing?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={handleSkipSaveAndNavigate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f1f1f1',
                  color: '#555',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9em',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleSaveAndNavigate}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9em',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Save & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brochure PDF Modal */}
      {showBrochureModal && (
        <ModalOverlay onClick={() => setShowBrochureModal(false)}>
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '90vw',
              maxWidth: '900px',
              height: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f8fafc'
            }}>
              <h3 style={{ margin: 0, color: '#1e40af', fontWeight: 600 }}>📄 MNGL DODO Brochure</h3>
              <button
                onClick={() => setShowBrochureModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
              >
                ✕
              </button>
            </div>
            <iframe
              src="/brochures/mngl-dodo-brochure.pdf"
              style={{
                flex: 1,
                width: '100%',
                border: 'none'
              }}
              title="MNGL DODO Brochure"
            />
          </div>
        </ModalOverlay>
      )}
    </>
  );
};

export default ApplicationFormPage3;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;

const ProgressBarContainerModal = styled.div`
  width: 100%;
  height: 20px;
  background-color: #e9ecef;
  border-radius: 10px;
  margin-top: 20px;
  overflow: hidden;
`;

const ProgressBarFillModal = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => props.progress}%; 
  background-color: #6cb288;
  transition: width 0.3s ease-in-out;
`;

const ProgressText = styled.p`
  margin-top: 10px;
  font-size: 0.9em;
  color: #555;
`;